// Reprojection of ESRI Shapefile-derived GeoJSON to WGS84 (EPSG:4326).
//
// The Places API conversion (`v2/places/esri/convert-to-geojson`) returns the
// coordinates in the shapefile's source CRS. The UI area math expects lon/lat
// degrees, so projected inputs (e.g. EPSG:3857 meters) produced huge areas.
// These helpers read the `.prj` from the uploaded ZIP and reproject with proj4.
//
// Every helper is defensive: on any failure the input is returned unchanged,
// which matches the behavior before this module existed.
import proj4 from 'proj4';
import JSZip from 'jszip';

const PRJ_RE = /\.prj$/i;
const SHP_RE = /\.shp$/i;
// Real .prj files are well under 1 KB. Skip anything bigger so a crafted ZIP
// can't make the browser inflate a huge entry.
const MAX_PRJ_BYTES = 64 * 1024;

// wkt-parser errors embed the whole WKT ("unable to parse string ..."), so only
// a short prefix of the message is logged.
const MAX_LOGGED_MESSAGE = 120;

function warnSkipped(err) {
  const message = err && err.message ? String(err.message).slice(0, MAX_LOGGED_MESSAGE) : 'unknown error';
  console.warn('ESRI reprojection skipped', message);
}

function basename(name) {
  const parts = name.split('/');
  return parts[parts.length - 1];
}

function stripExtension(name) {
  return name.replace(/\.[^./]+$/, '').toLowerCase();
}

function isIgnoredEntry(entry) {
  if (entry.dir) return true;
  const name = entry.name;
  if (name.startsWith('__MACOSX/') || name.includes('/__MACOSX/')) return true;
  return basename(name).startsWith('._');
}

/**
 * Read the `.prj` (CRS WKT) from a zipped ESRI Shapefile.
 * Prefers the `.prj` sharing the first `.shp`'s name, otherwise the first one.
 * Returns the trimmed WKT, or null when there is none or the ZIP is unreadable.
 */
export async function readPrjFromZip(arrayBufferOrBuffer) {
  try {
    if (!arrayBufferOrBuffer) return null;
    const zip = await JSZip.loadAsync(arrayBufferOrBuffer);
    const entries = Object.values(zip.files).filter((entry) => !isIgnoredEntry(entry));
    const prjEntries = entries.filter((entry) => PRJ_RE.test(entry.name));
    if (prjEntries.length === 0) return null;
    const shpEntry = entries.find((entry) => SHP_RE.test(entry.name));
    const shpKey = shpEntry ? stripExtension(shpEntry.name) : null;
    const prjEntry = prjEntries.find((entry) => stripExtension(entry.name) === shpKey) || prjEntries[0];
    // `_data.uncompressedSize` is the size declared in the ZIP's central
    // directory (JSZip 3.x internal). It is attacker-controlled, so it only
    // avoids inflating obviously oversized entries; a missing value is
    // rejected too, and the inflated text is checked again below.
    const declaredSize = prjEntry._data && prjEntry._data.uncompressedSize;
    if (typeof declaredSize !== 'number' || declaredSize > MAX_PRJ_BYTES) return null;
    const text = (await prjEntry.async('string')).trim();
    if (text.length > MAX_PRJ_BYTES) return null;
    return text || null;
  } catch (err) {
    return null;
  }
}

/** True when the WKT describes a geographic (lon/lat) CRS. Never throws. */
export function isGeographicPrj(prjWkt) {
  try {
    return proj4(prjWkt).oProj.projName === 'longlat';
  } catch (err) {
    return false;
  }
}

// Calls `visit(position)` for every position of a GeoJSON object, a geometry,
// or an array of those (the shpjs multi-layer shape).
function forEachPosition(node, visit) {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    node.forEach((child) => forEachPosition(child, visit));
    return;
  }
  if (node.type === 'FeatureCollection') {
    (node.features || []).forEach((feature) => forEachPosition(feature, visit));
  } else if (node.type === 'Feature') {
    forEachPosition(node.geometry, visit);
  } else if (node.type === 'GeometryCollection') {
    (node.geometries || []).forEach((geometry) => forEachPosition(geometry, visit));
  } else if (Array.isArray(node.coordinates)) {
    forEachCoordinate(node.coordinates, visit);
  }
}

function forEachCoordinate(coords, visit) {
  if (typeof coords[0] === 'number') {
    visit(coords);
    return;
  }
  coords.forEach((child) => {
    if (Array.isArray(child)) forEachCoordinate(child, visit);
  });
}

/** True when there is at least one position and all fit within lon/lat bounds. */
export function allCoordinatesLookGeographic(geojson) {
  let count = 0;
  let allGeographic = true;
  forEachPosition(geojson, (position) => {
    count += 1;
    if (!(Math.abs(position[0]) <= 180 && Math.abs(position[1]) <= 90)) allGeographic = false;
  });
  return count > 0 && allGeographic;
}

function hasPositions(geojson) {
  let found = false;
  forEachPosition(geojson, () => { found = true; });
  return found;
}

function omit(obj, keys) {
  const copy = {};
  Object.keys(obj).forEach((key) => {
    if (!keys.includes(key)) copy[key] = obj[key];
  });
  return copy;
}

function mapCoordinates(coords, mapPosition) {
  if (typeof coords[0] === 'number') return mapPosition(coords);
  return coords.map((child) => (Array.isArray(child) ? mapCoordinates(child, mapPosition) : child));
}

function mapGeometry(geometry, mapPosition, dropKeys) {
  if (!geometry || typeof geometry !== 'object') return geometry;
  if (geometry.type === 'GeometryCollection') {
    const copy = omit(geometry, dropKeys);
    copy.geometries = (geometry.geometries || []).map((child) => mapGeometry(child, mapPosition, ['bbox']));
    return copy;
  }
  if (Array.isArray(geometry.coordinates)) {
    const copy = omit(geometry, dropKeys);
    copy.coordinates = mapCoordinates(geometry.coordinates, mapPosition);
    return copy;
  }
  return geometry;
}

// Rebuilds the GeoJSON with every position mapped. `bbox` is dropped at every
// level (it would be stale) and `crs` at the top level of each layer.
function mapGeojson(node, mapPosition) {
  if (Array.isArray(node)) return node.map((child) => mapGeojson(child, mapPosition));
  if (!node || typeof node !== 'object') return node;
  const topKeys = ['bbox', 'crs'];
  if (node.type === 'FeatureCollection') {
    const copy = omit(node, topKeys);
    copy.features = (node.features || []).map((feature) => mapFeature(feature, mapPosition));
    return copy;
  }
  if (node.type === 'Feature') return mapFeature(node, mapPosition, topKeys);
  return mapGeometry(node, mapPosition, topKeys);
}

function mapFeature(feature, mapPosition, dropKeys = ['bbox']) {
  if (!feature || typeof feature !== 'object') return feature;
  const copy = omit(feature, dropKeys);
  copy.geometry = mapGeometry(feature.geometry, mapPosition, ['bbox']);
  return copy;
}

/**
 * Reproject GeoJSON from the CRS described by `prjWkt` to EPSG:4326.
 * Returns the input itself (same reference) when there is nothing to do: no
 * WKT, a WKT proj4 can't parse, a geographic CRS, no positions, or positions
 * that already look like lon/lat. Never throws.
 */
export function reprojectGeojsonToWgs84(geojson, prjWkt) {
  if (!prjWkt || !geojson || typeof geojson !== 'object') return geojson;
  try {
    if (isGeographicPrj(prjWkt)) return geojson;
    if (!hasPositions(geojson) || allCoordinatesLookGeographic(geojson)) return geojson;
    const converter = proj4(prjWkt, 'EPSG:4326');
    const mapPosition = (position) => {
      const [x, y, ...rest] = position;
      const [lon, lat] = converter.forward([x, y]);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
        throw new Error('non-finite reprojected coordinate');
      }
      return [lon, lat, ...rest];
    };
    return mapGeojson(geojson, mapPosition);
  } catch (err) {
    warnSkipped(err);
    return geojson;
  }
}

/**
 * Reproject the GeoJSON converted from an ESRI Shapefile ZIP to EPSG:4326,
 * using the ZIP's `.prj`. Returns the input unchanged on any failure.
 */
export async function reprojectEsriGeojson(geojson, zipArrayBuffer) {
  try {
    if (!geojson || typeof geojson !== 'object' || geojson.error) return geojson;
    const prjWkt = await readPrjFromZip(zipArrayBuffer);
    return reprojectGeojsonToWgs84(geojson, prjWkt);
  } catch (err) {
    warnSkipped(err);
    return geojson;
  }
}

/**
 * Post-process the payload of `v2/places/esri/convert-to-geojson`, which keeps
 * the shapefile's source CRS. `getZipArrayBuffer` is only called when the
 * payload is a non-empty object without `error`; if it throws or rejects, the
 * payload is returned unchanged. Never throws.
 */
export async function reprojectServerEsriPayload(payload, getZipArrayBuffer) {
  if (!payload || typeof payload !== 'object' || Object.keys(payload).length === 0 || payload.error) {
    return payload;
  }
  let zipArrayBuffer = null;
  try {
    zipArrayBuffer = await getZipArrayBuffer();
  } catch (err) {
    warnSkipped(err);
    return payload;
  }
  return reprojectEsriGeojson(payload, zipArrayBuffer);
}
